// 主题库扩展 6 风格前端皮肤辅助（044 T3.3）：consult / brutal / term / report / ink / memphis。
// 背景：v2geo.generated.ts 的 V2Skin.theme 快照经 dump 重跑后已含各主题全字段（带 # 色值），
// 但 V2Skin.style 仍为 string 类型——风格判定用运行时值，主题字段走本文件的收窄快照。
// 签名元素逐坐标对照后端 _shared/pptx/v2/layouts/（cover.ts / divider.ts / statement.ts /
// closing.ts / cards.ts / chrome.ts / memphis-motif.ts），禁止凭感觉写坐标。

import type { CSSProperties } from "react";
import type { V2Skin } from "../../lib/v2geo.generated";
import { px, pt } from "./shared";

// ---------- 风格判定（V2Skin.style 运行时值） ----------
export function isConsult(skin: V2Skin): boolean { return skin.style === "consult"; }
export function isBrutal(skin: V2Skin): boolean { return skin.style === "brutal"; }
export function isTerm(skin: V2Skin): boolean { return skin.style === "term"; }
export function isReport(skin: V2Skin): boolean { return skin.style === "report"; }
export function isInk(skin: V2Skin): boolean { return skin.style === "ink"; }
export function isMemphis(skin: V2Skin): boolean { return skin.style === "memphis"; }

// ---------- 主题快照（字段与 tokens.generated.ts 主题接口一致，色值带 #） ----------
export interface ConsultThemeSnapshot {
  paper: string; ink: string; muted: string; accent: string; accentOn: string;
  accentEmerald: string; accentGold: string; accentCoral: string; accentSlate: string;
}
export interface BrutalThemeSnapshot { paper: string; ink: string; muted: string; accent: string; accentOn: string; }
export interface TermThemeSnapshot {
  paper: string; ink: string; muted: string; inkDeep: string; accent: string; accentOn: string;
}
export interface ReportThemeSnapshot {
  paper: string; ink: string; muted: string; inkDeep: string; accent: string; accentOn: string; accentRed: string;
}
export interface InkThemeSnapshot {
  paper: string; ink: string; muted: string; accent: string; accentOn: string; accentGrey: string;
}
export interface MemphisThemeSnapshot {
  paper: string; ink: string; muted: string; accent: string; accentOn: string;
  accentBlue: string; accentRed: string; accentGreen: string; accentYellow: string;
}

// 收窄必须同时校验 theme.style 与 skin.style 都是目标风格——
// 仅查一致性会让 report 皮肤误命中 consultTheme/inkTheme 等（浏览器实测发现：report 封面冒出 ink 印章）。
const snap = <T,>(skin: V2Skin, style: string): T | null =>
  (skin.theme && (skin.theme as { style?: unknown }).style === style && skin.style === style)
    ? skin.theme as unknown as T : null;

export const consultTheme = (skin: V2Skin) => snap<ConsultThemeSnapshot>(skin, "consult");
export const brutalTheme = (skin: V2Skin) => snap<BrutalThemeSnapshot>(skin, "brutal");
export const termTheme = (skin: V2Skin) => snap<TermThemeSnapshot>(skin, "term");
export const reportTheme = (skin: V2Skin) => snap<ReportThemeSnapshot>(skin, "report");
export const inkTheme = (skin: V2Skin) => snap<InkThemeSnapshot>(skin, "ink");
export const memphisTheme = (skin: V2Skin) => snap<MemphisThemeSnapshot>(skin, "memphis");

/** memphis 5 色轮用（043 §4.7；渲染层直取 theme，不进 Skin 字段） */
export function memphisPalette(t: MemphisThemeSnapshot): string[] {
  return [t.accent, t.accentBlue, t.accentRed, t.accentGreen, t.accentYellow];
}

// ---------- 章节号（divider.ts roman/cnNum 1:1 移植） ----------
export function roman(n: number): string {
  const table: [number, string][] = [
    [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"], [100, "C"], [90, "XC"],
    [50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
  ];
  let r = ""; let v = n;
  for (const [k, s] of table) while (v >= k) { r += s; v -= k; }
  return r;
}

export function cnNum(n: number): string {
  const d = ["", "壹", "贰", "叁", "肆", "伍", "陆", "柒", "捌", "玖"];
  if (n <= 0) return String(n);
  if (n < 10) return d[n];
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return (tens > 1 ? d[tens] : "") + "拾" + (ones ? d[ones] : "");
}

// ---------- ink 朱印落款（cover.ts:113-123 / divider.ts:87-97 / closing.ts:73-81 同款方章） ----------
export function InkSeal({ skin, x, y, size = 0.56 }: { skin: V2Skin; x: number; y: number; size?: number }) {
  const t = inkTheme(skin);
  if (!t) return null;
  return (
    <>
      <div style={{
        position: "absolute", left: px(x), top: px(y), width: px(size), height: px(size),
        background: t.accent,
      }} />
      <div style={{
        position: "absolute", left: px(x), top: px(y), width: px(size), height: px(size),
        color: t.accentOn, fontFamily: skin.fontHead, fontSize: pt(size * 50),
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>印</div>
    </>
  );
}

// ---------- memphis 边距几何母题（memphis-motif.ts 1:1 移植；坐标硬编码在边距内，绝不压内容区） ----------
const MOTIF = {
  dot: (x: number, y: number, d: number, c: string): CSSProperties => ({
    position: "absolute", left: px(x), top: px(y), width: px(d), height: px(d), borderRadius: "50%", background: c,
  }),
  tri: (x: number, y: number, w: number, h: number, c: string): CSSProperties => ({
    position: "absolute", left: px(x), top: px(y), width: 0, height: 0,
    borderLeft: `${px(w / 2)} solid transparent`, borderRight: `${px(w / 2)} solid transparent`,
    borderTop: `${px(h)} solid ${c}`,
  }),
  // 之字折线：三个小矩形阶梯（左上→右上→右下）
  zig: (x: number, y: number, s: number, c: string): { style: CSSProperties; key: string }[] => {
    const seg = s / 3;
    return [
      { key: "z1", style: { position: "absolute", left: px(x), top: px(y), width: px(seg), height: px(0.035), background: c } },
      { key: "z2", style: { position: "absolute", left: px(x + seg), top: px(y), width: px(0.035), height: px(seg), background: c } },
      { key: "z3", style: { position: "absolute", left: px(x + seg), top: px(y + seg), width: px(seg * 2), height: px(0.035), background: c } },
    ];
  },
};

/** 母题狂欢（cover/divider/closing 同档 dense）；仅 memphis 渲染，父容器 overflow:hidden 裁切 */
export function MemphisMotif({ skin, variant: _variant }: { skin: V2Skin; variant: "cover" | "divider" | "closing" }) {
  const t = memphisTheme(skin);
  if (!t) return null;
  const pal = memphisPalette(t);
  const cream = t.paper;
  const onPaper = skin.bg === "paper";
  const zigs = (x: number, y: number, s: number, c: string, prefix: string) =>
    MOTIF.zig(x, y, s, c).map((z) => <div key={prefix + z.key} style={z.style} />);
  return (
    <>
      {/* 左上：大圆点（橙）+ 小圆（黄） */}
      <div style={MOTIF.dot(0.10, 0.10, 0.25, pal[0])} />
      <div style={MOTIF.dot(0.42, 0.06, 0.12, pal[4])} />
      {/* 右上：三角（蓝）+ 小圆（红） */}
      <div style={MOTIF.tri(12.82, 0.08, 0.32, 0.28, pal[1])} />
      <div style={MOTIF.dot(12.52, 0.40, 0.12, pal[2])} />
      {/* 左下：折线（绿）+ 小圆（奶油——深底上发光） */}
      {zigs(0.12, 7.26, 0.42, pal[3], "bl")}
      <div style={MOTIF.dot(0.62, 7.28, 0.15, cream)} />
      {/* 右下：大圆点（绿）+ 三角（橙） */}
      <div style={MOTIF.dot(12.88, 7.16, 0.22, pal[3])} />
      <div style={MOTIF.tri(12.52, 7.20, 0.26, 0.22, pal[0])} />
      {/* 上缘中央：折线（红/奶油） */}
      {zigs(5.9, 0.05, 0.42, onPaper ? pal[2] : cream, "tc")}
      {/* 下缘中央：小圆点（蓝/奶油） */}
      <div style={MOTIF.dot(6.45, 7.30, 0.13, onPaper ? pal[1] : cream)} />
    </>
  );
}
